import { CheckOutlined } from '@ant-design/icons';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessage } from 'components/ui';

import { type ImageProps } from '../Image';
import { StyledButton } from '../ImageViewer.style';
import { useImageViewerContext } from '../ImageViewerContext';

type ReviewedToolProps = {
  pictureId?: string;
  pictureGlobalId?: string;
  reviewed?: boolean;
  disabled?: boolean;
  refetch: ImageProps['refetch'];
};

export const ReviewedTool = ({
  pictureId,
  pictureGlobalId,
  reviewed = false,
  disabled = false,
  refetch,
}: ReviewedToolProps) => {
  const { t } = useTranslation();
  const message = useMessage();
  const { updatePictures, updatePicturesLoading } = useImageViewerContext();

  const handleClick = useCallback(async () => {
    if (!pictureId || !pictureGlobalId) {
      return;
    }

    const nextReviewed = !reviewed;

    try {
      await updatePictures({
        variables: {
          picturesToUpdate: [
            {
              globalId: pictureGlobalId,
              branchGlobalId: pictureId,
              reviewed: nextReviewed,
            },
          ],
        },
        update: (cache, { data }) => {
          if (data?.updatePictures) {
            cache.modify({
              id: cache.identify({ __typename: 'Picture', id: pictureId }),
              fields: {
                reviewed: () => nextReviewed,
              },
            });
          }
        },
      });

      // Refetching fullScreenImage query to update the reviewed status
      refetch();

      message.success(
        nextReviewed
          ? t('inspection.pictureMarkedAsReviewed', 'Picture marked as reviewed.')
          : t('inspection.pictureMarkedUnreviewed', 'Picture marked as unreviewed.')
      );
    } catch (_error) {
      message.error(t('inspection.picturesUpdateFailed', 'Failed to update pictures.'));
    }
  }, [message, pictureGlobalId, pictureId, refetch, reviewed, t, updatePictures]);

  return (
    <StyledButton
      data-testid="image-controls-reviewed"
      title={
        reviewed
          ? t('inspection.markUnreviewed', 'Mark unreviewed')
          : t('inspection.markAsReviewed', 'Mark as reviewed')
      }
      type={reviewed ? 'primary' : 'default'}
      onClick={handleClick}
      disabled={disabled || updatePicturesLoading}
      loading={updatePicturesLoading}
      icon={<CheckOutlined />}
    />
  );
};
